import { useEffect, useState } from "react";
import useAuth from "../../Users/hooks/useAuth";
import UploadText from "./UploadText";
import Slideshow from "./Slideshow";

const PresentationTitle = ({ presentation_title, successHandler }) => {
    const [listFiles, setListFiles] = useState([]);
    const { auth, setAuth } = useAuth();
    const [updated, setUpdated] = useState(false);
    const [transcript, setTranscript] = useState();
    const [slideshow, setSlideshow] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(new Set());

    const fetchData = async (route, method, outdata) => {
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/presentation/${route}`;
        const datatosend = JSON.stringify(outdata);
        
        try {
            const response = await fetch(url, {
                method: method,
                body: datatosend,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            
            if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
                return null;
            }
            
            if (response.ok) {
                const json = await response.json();
                return json;
            }
            
            throw new Error(`Request failed with status: ${response.status}`);
        } catch (error) {
            console.error("API request failed:", error);
            throw error;
        }
    };

    const getFiles = async (title) => {
        setIsLoading(true);
        try {
            const json = await fetchData(`list-texts`, "POST", title);
            if (json) {
                setListFiles(json);
                setUpdated(false);
            }
        } catch (error) {
            console.error("Failed to fetch files:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUploadSuccess = async (fileInfo) => {
        const fileKey = `${fileInfo.name}-${Date.now()}`;
        setUploadingFiles(prev => new Set([...prev, fileKey]));
        
        try {
            setUpdated(true);
            await new Promise(resolve => setTimeout(resolve, 1000));
            await getFiles(presentation_title);
        } catch (error) {
            console.error("Failed to refresh files after upload:", error);
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(fileKey);
                return newSet;
            });
        }
    };

    const handleUploadError = (fileKey, error) => {
        console.error("Upload failed:", error);
        setUploadingFiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(fileKey);
            return newSet;
        });
    };

    useEffect(() => {
        getFiles(presentation_title);
    }, [presentation_title, updated]);

    const handleCloseTranscript = () => {
        setTranscript(null);
    };

    const handleCloseSlideshow = () => {
        setSlideshow(null);
    };

    const handleViewTranscript = (file) => {
        setSlideshow(null);
        setTranscript({
            transcript: file.transcript,
            language: file.language,
            title: file.title
        });
    };

    const handleViewSlideshow = (file) => {
        setTranscript(null);
        setSlideshow({
            transcript: file.transcript,
            version: file.version,
            title: file.title,
            language: file.language
        });
    };

    const isAnyUploadInProgress = uploadingFiles.size > 0;

    // If slideshow is active, render only the slideshow in full screen
    if (slideshow) {
        return (
            <div className="fixed inset-0 bg-white z-50 overflow-auto">
                <div className="min-h-screen p-4">
                    <Slideshow 
                        transcript={slideshow.transcript} 
                        title={slideshow.title} 
                        version={slideshow.version} 
                        language={slideshow.language} 
                        onClose={handleCloseSlideshow}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col bg-white rounded-xl shadow-lg p-6 mx-auto w-full max-w-6xl">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <button 
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                    onClick={() => successHandler(null)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <h1 className="text-2xl font-bold text-gray-800 text-center flex-1">
                    Presentation: {presentation_title.title}
                </h1>
                <div className="w-10"></div>
            </div>

            {/* Upload Progress Indicator */}
            {isAnyUploadInProgress && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        <p className="text-blue-700 text-sm">
                            Uploading {uploadingFiles.size} file(s)... Please wait.
                        </p>
                    </div>
                </div>
            )}

            {/* Main Content Grid */}
            <div className={`grid ${transcript ? "lg:grid-cols-2" : "grid-cols-1"} gap-8`}>
                {/* Left Column - Upload and File List */}
                <div className="flex flex-col space-y-6">
                    {/* Upload Section */}
                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
                        <h2 className="text-xl font-semibold text-blue-600 text-center mb-4">
                            Upload text and generate transcriptions
                        </h2>
                        <UploadText 
                            book={presentation_title} 
                            successHandler={handleUploadSuccess}
                            errorHandler={handleUploadError}
                            isUploading={isAnyUploadInProgress}
                        />
                    </div>

                    {/* File List Section */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold text-blue-500">
                                Presentation Text Variants
                            </h3>
                            <button 
                                onClick={() => getFiles(presentation_title)}
                                disabled={isLoading || isAnyUploadInProgress}
                                className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Refresh
                            </button>
                        </div>
                        
                        {isLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                                <p className="text-gray-600 mt-2">Loading files...</p>
                            </div>
                        ) : listFiles && listFiles.length > 0 ? (
                            <div className="space-y-4">
                                {listFiles.map((file, index) => (
                                    <div 
                                        key={`${file.title}-${file.language}-${file.version}-${index}`}
                                        className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-800">
                                                    Version {index + 1}
                                                </h4>
                                                <div className="flex gap-4 text-sm text-gray-600 mt-1">
                                                    <span>Language: {file.language}</span>
                                                    <span>Version: {file.version}</span>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button 
                                                    className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={() => handleViewTranscript(file)}
                                                    disabled={isAnyUploadInProgress}
                                                >
                                                    View Transcript
                                                </button>
                                                <button 
                                                    className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={() => handleViewSlideshow(file)}
                                                    disabled={isAnyUploadInProgress}
                                                >
                                                    Start Practice
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                <p className="text-gray-600">No text variants available.</p>
                                <p className="text-gray-500 text-sm mt-1">Upload a text file to get started.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Transcript */}
                {transcript && (
                    <div className="flex flex-col">
                        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 h-full">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-semibold text-blue-500">Transcript</h3>
                                <button 
                                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    onClick={handleCloseTranscript}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            
                            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                                {transcript.transcript && transcript.transcript.hasOwnProperty("segments") ? (
                                    <div className="space-y-3">
                                        {transcript.transcript.segments.map((item, index) => (
                                            <div 
                                                key={index}
                                                className="bg-white rounded-lg p-3 shadow-sm border border-gray-200"
                                            >
                                                <p className="text-gray-700">{item}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-gray-600">No transcript segments available.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PresentationTitle;